import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import GridWrapper from 'src/components/GridWrapper';
import { IPO } from 'src/types/PO';
import { modifyRequest } from 'src/utils/requestInterceptor';

import uuid from 'react-uuid';

import { State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import useReceiptColumns from 'src/assets/static/new_receipt/useReceiptColumns';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import { BlindReceivePermissions } from 'src/components/guards/PermissionsList';
import { fetchReceiptGridData } from 'src/features/ReceiptGridSlice';
import usePermission from 'src/hooks/usePermissions';
import useWindowSize from 'src/hooks/useWindowSize';
import { IDisableStep } from 'src/pages/EnterReceipts';
import { IReceipt } from 'src/types/Receipt';
import { SetState } from 'src/types/generic';
import InvoiceGridToolbar from '../enter_invoice/InvoiceGridToolBar';
import ReceiptLineItemWizard from './enter_receipt_wizard/ReceiptLineItemWizard';

const DATA_ITEM_KEY: string = 'receivingReceiptId';
const DATE_FIELD = 'creationDate';
const dateArr = [DATE_FIELD];

const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [],
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Receipts',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface ReceiptGridPops {
  companyId: string;
  selectedReceiptPoRow: IPO | null;
  setSelectedReceiptRow: SetState<IReceipt | null>;
  setDisableStep?: SetState<IDisableStep>;
  showSaveLineItemButton?: boolean;
  isSelectRowRequired?: boolean;
  isDetailsPage: boolean;
  key?: string;
}

const ReceiptGrid: React.FC<ReceiptGridPops> = ({
  companyId,
  selectedReceiptPoRow,
  setSelectedReceiptRow,
  setDisableStep,
  showSaveLineItemButton = true,
  isSelectRowRequired = true,
  isDetailsPage = false,
  key,
}) => {
  // Po state
  const dispatch = useAppDispatch();
  const [dataState, setDataState] = useState(initialDataState);
  const [receiptListData, setReceiptListData] = useState<IReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const receiptColumns = useReceiptColumns();
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const listCompanyReceipt = useCallback(
    (companyId: string) => {
      const data = { ...modifyRequest(dataState, dateArr, receiptColumns) };
      const poId = selectedReceiptPoRow?.poId.toString();
      if (poId) {
        const filterReceiptByPoId = { field: 'poId', value: poId, operator: 'eq' };
        data.filters = [...data.filters, filterReceiptByPoId];
      }
      return dispatch(
        fetchReceiptGridData({
          data,
          companyId,
        })
      );
    },
    [dataState, dispatch, receiptColumns, selectedReceiptPoRow?.poId]
  );

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setDisableStep?.((prev) => ({ ...prev, disablePrev: true, disableNext: true }));
    listCompanyReceipt(companyId)
      .unwrap()
      .then((res) => {
        const response = res?.items?.map((item: IPO[]) => ({
          ...item,
          rowId: uuid(),
          selected: false,
        }));
        setTotalRecords(res?.totalRecords);
        setReceiptListData(response);
      })
      .finally(() => {
        setLoading(false);
        setDisableStep?.((prev) => ({ ...prev, disablePrev: false, disableNext: false }));
      });
  }, [companyId, listCompanyReceipt, setDisableStep]);

  useEffect(() => {
    setSelectedReceiptRow(receiptListData?.find((item) => item.selected) || null);
  }, [receiptListData, setSelectedReceiptRow]);

  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  }, []);

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    setReceiptListData((prev: IReceipt[]) => {
      const prevItems = [...prev];
      const index = event.startRowIndex;
      const selectedList: IReceipt[] = prevItems?.map((item: IReceipt, i) => ({
        ...item,
        selected: index === i,
      }));
      return selectedList;
    });
  }, []);

  // clear filter
  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
    setSelectedReceiptRow(null);
  }, [setSelectedReceiptRow]);

  // grid  line item cell click
  const [receiptLineItemsData, setReceiptLineItemsData] = useState<IReceipt>({} as IReceipt);
  const toggleLineItemModal = useCallback(() => {
    setLineItemModal((prev) => !prev);
  }, []);
  const handleSaveLineItem = () => {
    const selectedReceiptId = receiptLineItemsData?.receivingReceiptId || '';
    setReceiptListData((prev) =>
      prev.map((item) => {
        if (item.receivingReceiptId === selectedReceiptId) {
          return { ...item, selected: true };
        }
        return { ...item, selected: false };
      })
    );
    setSelectedReceiptRow(receiptLineItemsData);
    toggleLineItemModal();
  };

  const cellClick = useCallback(
    (data: IReceipt) => {
      setReceiptLineItemsData(data);
      toggleLineItemModal();
    },
    [toggleLineItemModal]
  );

  //Line item modal
  const [lineItemModal, setLineItemModal] = useState<boolean>(false);

  const { height } = useWindowSize();
  const calculateGridHeight = () => `${height - 198}px`;
  const handleClearSelection = useCallback(() => {
    setReceiptListData((prev) => prev.map((item) => ({ ...item, selected: false })));
  }, []);

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <InvoiceGridToolbar
        clearFilter={handleClearFilter}
        clearSelection={handleClearSelection}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [handleClearFilter, handleClearSelection, isClearFiltersDisabled]
  );
  const poSummary = () => {
    const poQty = selectedReceiptPoRow?.poQty || 0;
    const receiptTTLQty = selectedReceiptPoRow?.receiptTTLQty || 0;
    return `Summary-Quantity: Ordered ${poQty} | Received ${receiptTTLQty} Variance ${
      poQty - receiptTTLQty
    }`;
  };
  const isBlindReceive = usePermission({ permissions: BlindReceivePermissions });
  return (
    <>
      <div className="mt-2">
        <div className="flex items-center">
          <p className="font-bold text-sm">
            Showing Receipts for PO # "{selectedReceiptPoRow?.poNum}"
          </p>
          {!isBlindReceive && (
            <div className="ml-2 h-8 shadow border border-solid border-gray-300 flex items-center">
              <p className="text-sm font-bold p-2">{poSummary()}</p>
            </div>
          )}
        </div>
        <Dialog width="80%" open={lineItemModal} onClose={toggleLineItemModal} title="">
          <ReceiptLineItemWizard
            title={`Showing items for Receipt # ${receiptLineItemsData?.receivingReceiptId || ''}`}
            receiptId={receiptLineItemsData?.receivingReceiptId}
          />
          <DialogActionsBar>
            <div className="flex justify-end gap-2">
              {showSaveLineItemButton && (
                <Button color="info" onClick={handleSaveLineItem}>
                  Exit and Select Record
                </Button>
              )}
              <Button color="info" onClick={toggleLineItemModal}>
                Exit
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
        <GridWrapper
          columns={receiptColumns}
          dataSource={receiptListData}
          dataState={dataState}
          height={
            isDetailsPage
              ? height >= 950
                ? `${height - 468}px`
                : height >= 860
                ? `${height - 406}px`
                : `${height - 398}px`
              : calculateGridHeight()
          }
          total={totalRecords}
          onDataStateChange={onDataStateChange}
          onSelectionChange={isSelectRowRequired ? onSelectionChange : () => {}}
          gridToolbarChildren={isSelectRowRequired ? gridToolbarChildren : ''}
          cellClick={() => ({ cellClick })}
          loading={loading}
          {...defaultProps}
          key={key}
        />
      </div>
    </>
  );
};

export default ReceiptGrid;
